import React, { useState } from 'react';
import { ref, set, getDatabase } from 'firebase/database';
import { app } from '../firebase';
import { UNIVERSIDADES_EXANI, MODULOS_CENEVAL, AREAS_TRANSVERSALES, CARRERAS_POR_UNIVERSIDAD, CATALOGO_CARRERAS_GENERICO } from '../constants/universitiesData';
import { EXANI_TAXONOMY } from '../constants/exaniConstants';

const AdminSeedUniversities = () => {
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);

  const addLog = (msg, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, msg, type }]);
  };

  const seedData = async () => {
    if (status === 'running') return;
    
    setStatus('running');
    setError(null);
    setLogs([]);
    
    const db = getDatabase(app);
    const steps = [
      { key: 'universidades', data: UNIVERSIDADES_EXANI, path: 'universidades', transform: (items) => {
        const obj = {};
        items.forEach(item => { obj[item.id] = { ...item, createdAt: Date.now(), updatedAt: Date.now() }; });
        return obj;
      }},
      { key: 'modulosCeneval', data: Object.entries(MODULOS_CENEVAL), path: 'modulosCeneval', transform: (items) => {
        const obj = {};
        items.forEach(([key, modulo]) => { obj[key] = { ...modulo, createdAt: Date.now() }; });
        return obj;
      }},
      { key: 'areasTransversales', data: AREAS_TRANSVERSALES, path: 'areasTransversales', transform: (items) => {
        const obj = {};
        items.forEach(item => { obj[item.id] = { ...item, createdAt: Date.now() }; });
        return obj;
      }},
      { key: 'catalogoCarreras', data: CATALOGO_CARRERAS_GENERICO, path: 'catalogoCarreras', transform: (items) => {
        const obj = {};
        items.forEach(item => { obj[item.id] = { ...item, createdAt: Date.now() }; });
        return obj;
      }},
      { key: 'universidadesCarreras', data: null, path: 'universidadesCarreras', custom: true },
      { key: 'exaniTaxonomy', data: EXANI_TAXONOMY, path: 'config/exaniTaxonomy', transform: (data) => ({ ...data, version: data.version, lastUpdated: Date.now(), seededAt: Date.now() }) },
      { key: 'indices', data: null, path: 'indices', custom: true },
      { key: 'seedVersion', data: null, path: 'config/seedVersion', custom: true }
    ];

    setProgress({ current: 0, total: steps.length, message: 'Iniciando...' });

    try {
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        setProgress({ current: i + 1, total: steps.length, message: step.key });
        addLog(`Procesando: ${step.key}...`);

        let dataToWrite;
        
        if (step.custom) {
          if (step.key === 'universidadesCarreras') {
            // UNINAV específico + universidades principales genéricas
            const uniCarrerasData = {};
            uniCarrerasData.UNINAV = CARRERAS_POR_UNIVERSIDAD.UNINAV.map(c => ({ ...c, universidadId: 'UNINAV', createdAt: Date.now() }));
            
            const unisPrincipales = ['UANL', 'UNAM', 'IPN', 'UAM', 'UV', 'UADY', 'UASLP', 'UAEMEX'];
            for (const uniId of unisPrincipales) {
              if (uniId === 'UNINAV') continue;
              const uni = UNIVERSIDADES_EXANI.find(u => u.id === uniId);
              if (!uni) continue;
              uniCarrerasData[uniId] = CATALOGO_CARRERAS_GENERICO.slice(0, 20).map(c => ({ ...c, universidadId: uniId, createdAt: Date.now() }));
            }
            dataToWrite = uniCarrerasData;
          } else if (step.key === 'indices') {
            // Índices de búsqueda
            const uniPorEstado = {};
            UNIVERSIDADES_EXANI.forEach(uni => {
              if (!uniPorEstado[uni.estado]) uniPorEstado[uni.estado] = [];
              uniPorEstado[uni.estado].push(uni.id);
            });
            
            const carrerasPorCategoria = {};
            CATALOGO_CARRERAS_GENERICO.forEach(carrera => {
              if (!carrerasPorCategoria[carrera.categoria]) carrerasPorCategoria[carrera.categoria] = [];
              carrerasPorCategoria[carrera.categoria].push(carrera.id);
            });
            
            const carrerasPorModulo = {};
            CATALOGO_CARRERAS_GENERICO.forEach(carrera => {
              (carrera.modulosSugeridos || []).forEach(modulo => {
                if (!carrerasPorModulo[modulo]) carrerasPorModulo[modulo] = [];
                carrerasPorModulo[modulo].push(carrera.id);
              });
            });
            
            dataToWrite = {
              universidadesPorEstado: uniPorEstado,
              carrerasPorCategoria: carrerasPorCategoria,
              carrerasPorModulo: carrerasPorModulo
            };
          } else if (step.key === 'seedVersion') {
            dataToWrite = {
              version: '2027-1',
              fecha: new Date().toISOString(),
              timestamp: Date.now(),
              descripcion: 'Seed inicial universidades y carreras EXANI-II 2027 (vía Admin Panel)',
              conteos: {
                universidades: UNIVERSIDADES_EXANI.length,
                modulosCeneval: Object.keys(MODULOS_CENEVAL).length,
                areasTransversales: AREAS_TRANSVERSALES.length,
                carrerasCatalogo: CATALOGO_CARRERAS_GENERICO.length,
                universidadesConCarreras: 9 // UNINAV + 8 principales
              }
            };
          }
        } else {
          dataToWrite = step.transform(step.data);
        }

        await set(ref(db, step.path), dataToWrite);
        addLog(`✅ ${step.key}: ${Object.keys(dataToWrite).length} registros en /${step.path}`, 'success');
      }

      addLog('🎉 ¡SEMBRADO COMPLETADO EXITOSAMENTE!', 'success');
      setStatus('completed');
      
    } catch (err) {
      addLog(`❌ Error: ${err.message}`, 'error');
      setError(err.message);
      setStatus('error');
    }
  };

  const clearData = async () => {
    if (!window.confirm('¿Eliminar TODOS los datos de universidades/carreras? Esta acción no se puede deshacer.')) return;
    
    setStatus('running');
    setLogs([]);
    
    const db = getDatabase(app);
    const paths = [
      'universidades',
      'modulosCeneval', 
      'areasTransversales',
      'catalogoCarreras',
      'universidadesCarreras',
      'config/exaniTaxonomy',
      'indices/universidadesPorEstado',
      'indices/carrerasPorCategoria',
      'indices/carrerasPorModulo',
      'config/seedVersion'
    ];

    try {
      for (const path of paths) {
        await set(ref(db, path), null);
        addLog(`🗑️ Eliminado: /${path}`, 'warning');
      }
      addLog('✅ Limpieza completada', 'success');
      setStatus('idle');
    } catch (err) {
      addLog(`❌ Error: ${err.message}`, 'error');
      setError(err.message);
      setStatus('error');
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '16px', color: 'var(--admin-text-custom, #fff)' }}>
        🌱 Sembrado de Universidades y Carreras EXANI-II
      </h2>
      
      <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0, color: 'var(--admin-text-custom, #fff)' }}>Datos a sembrar:</h3>
        <ul style={{ color: 'rgba(255,255,255,0.8)', lineHeight: '1.8' }}>
          <li><strong>Universidades:</strong> {UNIVERSIDADES_EXANI.length} instituciones (públicas, privadas, militares)</li>
          <li><strong>Módulos CENEVAL:</strong> {Object.keys(MODULOS_CENEVAL).length} módulos (16 específicos + 2 generales)</li>
          <li><strong>Áreas Transversales:</strong> {AREAS_TRANSVERSALES.length} áreas obligatorias (65% del examen)</li>
          <li><strong>Catálogo Carreras:</strong> {CATALOGO_CARRERAS_GENERICO.length} carreras con módulos sugeridos</li>
          <li><strong>Carreras UNINAV:</strong> {CARRERAS_POR_UNIVERSIDAD.UNINAV.length} carreras específicas</li>
          <li><strong>Índices:</strong> Búsqueda por estado, categoría y módulo</li>
          <li><strong>Taxonomía EXANI:</strong> Estructura completa validada</li>
        </ul>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={seedData}
          disabled={status === 'running'}
          style={{
            padding: '12px 24px',
            background: status === 'running' ? '#666' : 'var(--admin-btn-custom, #2196F3)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: status === 'running' ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {status === 'running' ? '⏳ Sembrando...' : '🌱 Ejecutar Sembrado'}
        </button>
        
        <button
          onClick={clearData}
          disabled={status === 'running'}
          style={{
            padding: '12px 24px',
            background: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: status === 'running' ? 'not-allowed' : 'pointer',
            fontSize: '16px'
          }}
        >
          🗑️ Limpiar Todo
        </button>
      </div>

      {status === 'running' && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            height: '8px', 
            background: 'rgba(255,255,255,0.1)', 
            borderRadius: '4px', 
            overflow: 'hidden' 
          }}>
            <div style={{
              width: `${(progress.current / progress.total) * 100}%`,
              height: '100%',
              background: 'var(--admin-btn-custom, #2196F3)',
              transition: 'width 0.3s'
            }} />
          </div>
          <p style={{ margin: '8px 0 0', color: 'rgba(255,255,255,0.7)' }}>
            Paso {progress.current} de {progress.total}: {progress.message}
          </p>
        </div>
      )}

      {error && (
        <div style={{ 
          background: 'rgba(244,67,54,0.2)', 
          border: '1px solid #f44336', 
          borderRadius: '8px', 
          padding: '16px',
          marginBottom: '20px',
          color: '#ffcdd2'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {status === 'completed' && (
        <div style={{ 
          background: 'rgba(76,175,80,0.2)', 
          border: '1px solid #4caf50', 
          borderRadius: '8px', 
          padding: '16px',
          marginBottom: '20px',
          color: '#c8e6c9'
        }}>
          <strong>✅ Completado:</strong> Todos los datos sembrados en RTDB. Verifica en Firebase Console.
        </div>
      )}

      <div style={{ 
        background: 'rgba(0,0,0,0.3)', 
        borderRadius: '8px', 
        maxHeight: '400px', 
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: '13px'
      }}>
        {logs.length === 0 && (
          <div style={{ padding: '20px', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
            Los logs aparecerán aquí durante el sembrado...
          </div>
        )}
        {logs.map((log, idx) => (
          <div key={idx} style={{ 
            padding: '6px 12px', 
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            color: log.type === 'success' ? '#81c784' : log.type === 'error' ? '#ef9a9a' : log.type === 'warning' ? '#ffcc80' : '#fff'
          }}>
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>[{log.timestamp}]</span> {' '}
            {log.msg}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminSeedUniversities;